import { PRODUCTS_TABLE, SUPABASE_ANON_KEY, SUPABASE_URL } from '../../config/supabase';
import type { CatalogProduct, MacroCategory } from './types';

const PRODUCT_SELECT = 'id,name,category,company,image,featured,in_stock,unit,created_at';

function apiHeaders(): HeadersInit {
  return {
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json'
  };
}

export function normalizeMacroKey(value: unknown): string {
  return String(value || 'Altro')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'altro';
}

export function formatCategoryTitle(value: unknown): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Altro';

  return raw
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((word) => {
      const lowerParticles = ['di', 'da', 'de', 'del', 'della', 'delle', 'dei', 'per', 'e', 'in'];
      if (lowerParticles.includes(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ')
    .replace(/\bDpi\b/g, 'DPI')
    .replace(/\bPvc\b/g, 'PVC')
    .replace(/\bPe\b/g, 'PE')
    .replace(/\bPp\b/g, 'PP');
}

export async function fetchCatalogProducts(): Promise<CatalogProduct[]> {
  const url = `${SUPABASE_URL}/rest/v1/${PRODUCTS_TABLE}?select=${encodeURIComponent(PRODUCT_SELECT)}&order=created_at.desc`;

  const response = await fetch(url, {
    method: 'GET',
    headers: apiHeaders()
  });

  if (!response.ok) {
    const message = await response.text().catch(() => '');
    throw new Error(`Errore caricamento macro-categorie catalogo: ${message || response.statusText}`);
  }

  const data = (await response.json()) as CatalogProduct[];
  return Array.isArray(data) ? data : [];
}

function productScore(product: CatalogProduct): number {
  let score = 0;
  if (product.featured) score += 100;
  if (product.image) score += 20;
  if (product.in_stock !== false) score += 10;
  return score;
}

export function buildMacroCategories(products: CatalogProduct[]): MacroCategory[] {
  const groups = new Map<string, MacroCategory>();

  for (const product of products) {
    const title = formatCategoryTitle(product.category);
    const key = normalizeMacroKey(title);
    const current = groups.get(key) || {
      id: key,
      title,
      count: 0,
      companies: [],
      products: [],
      preview: []
    };

    current.count += 1;
    current.products.push(product);

    const company = String(product.company || '').trim();
    if (company && !current.companies.includes(company)) current.companies.push(company);

    groups.set(key, current);
  }

  return Array.from(groups.values())
    .map((macro) => {
      const sortedProducts = [...macro.products].sort((a, b) => productScore(b) - productScore(a));
      return {
        ...macro,
        companies: macro.companies.sort((a, b) => a.localeCompare(b)).slice(0, 4),
        products: sortedProducts,
        preview: sortedProducts.slice(0, 6)
      };
    })
    .sort((a, b) => b.count - a.count || a.title.localeCompare(b.title));
}
